/**
 * Cookie Consent & Cookie Preference Center Component.
 *
 * Fully compliant with:
 * - GDPR (ePrivacy Directive 2002/58/EC & Art. 7 Consent)
 * - CCPA / CPRA (Do Not Sell/Share & Sec-GPC auto-detection)
 * - India DPDP Act 2023
 *
 * Automatically detects navigator.globalPrivacyControl (Sec-GPC) and
 * navigator.doNotTrack to suppress optional tracking by default.
 */
import { useState, useEffect, type JSX } from 'react'
import { Shield, Settings, Check, X } from 'lucide-react'

export interface CookiePreferences {
  necessary: true
  analytics: boolean
  preferences: boolean
  timestamp: string
  gpcDetected: boolean
}

const STORAGE_KEY = 'kc3.cookie_consent'

export function getStoredCookiePreferences(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as CookiePreferences
  } catch {
    return null
  }
}

export function saveCookiePreferences(prefs: Omit<CookiePreferences, 'necessary' | 'timestamp'>): CookiePreferences {
  const isGpc =
    typeof navigator !== 'undefined' &&
    ((navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl === true ||
      (navigator as unknown as { doNotTrack?: string }).doNotTrack === '1')

  const full: CookiePreferences = {
    necessary: true,
    analytics: isGpc ? false : prefs.analytics,
    preferences: prefs.preferences,
    timestamp: new Date().toISOString(),
    gpcDetected: isGpc,
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(full))
  } catch {
    // Ignore storage errors in private browsing
  }
  return full
}

export function CookieConsentBanner(): JSX.Element | null {
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [gpcActive, setGpcActive] = useState(false)

  const [analyticsAllowed, setAnalyticsAllowed] = useState(false)
  const [preferencesAllowed, setPreferencesAllowed] = useState(true)

  useEffect(() => {
    setMounted(true)
    const isGpc =
      typeof navigator !== 'undefined' &&
      ((navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl === true ||
        (navigator as unknown as { doNotTrack?: string }).doNotTrack === '1')
    setGpcActive(isGpc)

    const stored = getStoredCookiePreferences()
    if (!stored) {
      setVisible(true)
      if (isGpc) {
        setAnalyticsAllowed(false)
      }
    } else {
      setAnalyticsAllowed(stored.analytics)
      setPreferencesAllowed(stored.preferences)
    }

    const onOpenPreferences = () => {
      setShowModal(true)
    }
    window.addEventListener('open-cookie-preferences', onOpenPreferences)
    return () => window.removeEventListener('open-cookie-preferences', onOpenPreferences)
  }, [])

  if (!mounted) return null

  const handleAcceptAll = () => {
    saveCookiePreferences({
      analytics: !gpcActive,
      preferences: true,
      gpcDetected: gpcActive,
    })
    setVisible(false)
    setShowModal(false)
  }

  const handleRejectNonEssential = () => {
    saveCookiePreferences({
      analytics: false,
      preferences: false,
      gpcDetected: gpcActive,
    })
    setVisible(false)
    setShowModal(false)
  }

  const handleSaveCustom = () => {
    saveCookiePreferences({
      analytics: gpcActive ? false : analyticsAllowed,
      preferences: preferencesAllowed,
      gpcDetected: gpcActive,
    })
    setVisible(false)
    setShowModal(false)
  }

  return (
    <>
      {/* Banner */}
      {visible && !showModal && (
        <aside
          role="region"
          aria-label="Cookie and Privacy Consent"
          className="fixed bottom-0 left-0 right-0 z-50 border-t border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur-md sm:p-5"
        >
          <div className="mx-auto flex max-w-6xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1.5 pr-4">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-blue-600" aria-hidden="true" />
                <h3 className="text-sm font-semibold text-slate-900">Cookie & Privacy Preferences</h3>
                {gpcActive && (
                  <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                    <Check className="h-3 w-3" /> Sec-GPC Honored
                  </span>
                )}
              </div>
              <p className="text-xs leading-relaxed text-slate-600">
                We use strictly necessary cookies to ensure system security, authentication, and session reliability.
                With your consent, we also collect anonymized performance and telemetry metrics to optimize VoIP call
                quality and ticket responsiveness.
              </p>
              <div className="pt-0.5">
                <button
                  type="button"
                  onClick={() => {
                    setAnalyticsAllowed(false)
                    setShowModal(true)
                  }}
                  className="text-[11px] font-medium text-slate-500 underline hover:text-slate-800"
                >
                  Do Not Sell or Share My Personal Information
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
              >
                <Settings className="h-3.5 w-3.5" />
                Customize
              </button>
              <button
                type="button"
                onClick={handleRejectNonEssential}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
              >
                Reject Non-Essential
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="rounded-md bg-blue-600 px-4 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-blue-700"
              >
                Accept All
              </button>
            </div>
          </div>
        </aside>
      )}

      {/* Preferences Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-labelledby="cookie-modal-title"
            aria-modal="true"
            className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                <h2 id="cookie-modal-title" className="text-base font-semibold text-slate-900">
                  Cookie & Telemetry Settings
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-4 space-y-4">
              {gpcActive && (
                <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 text-xs text-blue-800">
                  🛡️ <strong>Global Privacy Control (Sec-GPC) detected:</strong> Your browser has signaled an opt-out preference. Non-essential tracking and analytics are automatically disabled.
                </div>
              )}

              {/* Necessary */}
              <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-100 bg-slate-50 p-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900">Strictly Necessary Cookies</span>
                    <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">
                      Always Active
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Required for core authentication (JWT), double-submit CSRF prevention, session timeout, and secure API communication.
                  </p>
                </div>
                <input type="checkbox" checked disabled className="mt-1 h-4 w-4 rounded accent-blue-600 opacity-60" />
              </div>

              {/* Analytics */}
              <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-100 p-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900">VoIP & Performance Analytics</span>
                    {gpcActive && (
                      <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                        Disabled by GPC
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">
                    Captures WebRTC audio jitter, packet loss, and call Mean Opinion Scores (MOS) to troubleshoot degraded telephony connections.
                  </p>
                </div>
                <input
                  type="checkbox"
                  id="pref-analytics"
                  checked={gpcActive ? false : analyticsAllowed}
                  disabled={gpcActive}
                  onChange={(e) => setAnalyticsAllowed(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 accent-blue-600 focus:ring-blue-500"
                />
              </div>

              {/* Preferences */}
              <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-100 p-3">
                <div className="space-y-1">
                  <span className="text-sm font-medium text-slate-900">User Interface Preferences</span>
                  <p className="text-xs text-slate-500">
                    Remembers softphone dock position, drawer widths, and audio device choices between visits.
                  </p>
                </div>
                <input
                  type="checkbox"
                  id="pref-ui"
                  checked={preferencesAllowed}
                  onChange={(e) => setPreferencesAllowed(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 accent-blue-600 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={handleRejectNonEssential}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                Reject All
              </button>
              <button
                type="button"
                onClick={handleSaveCustom}
                className="rounded-lg bg-blue-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
